import React, { useState, useRef, useEffect } from "react";
import { Box, Flex, Icon } from "@chakra-ui/react";
import { FaGripVertical } from "react-icons/fa";

interface SplitViewProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  isRightPanelVisible: boolean;
  defaultRightWidth?: number;
}

const SplitView: React.FC<SplitViewProps> = ({
  leftPanel,
  rightPanel,
  isRightPanelVisible,
  defaultRightWidth = 50,
}) => {
  const [dividerPos, setDividerPos] = useState(defaultRightWidth);
  const isDragging = useRef(false);

  useEffect(() => {
    if (isRightPanelVisible) {
      setDividerPos(defaultRightWidth);
    }
  }, [isRightPanelVisible]);

  const startDrag = () => { 
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  
  const stopDrag = () => { 
    isDragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  const onDrag = (e: MouseEvent) => {
    if (!isDragging.current) return;
    const newPos = (e.clientX / window.innerWidth) * 100;
    if (newPos > 20 && newPos < 80) setDividerPos(newPos);
  };

  useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, []);

  // Reset divider position when panel becomes visible
  useEffect(() => {
    if (isRightPanelVisible && dividerPos < 20) {
      setDividerPos(defaultRightWidth);
    }
  }, [ defaultRightWidth, dividerPos]);

  const leftWidth = isRightPanelVisible ? dividerPos : 100;
  const rightWidth = isRightPanelVisible ? 100 - dividerPos : 0;

  return (
    <Flex
      height="100%"
      width="100%"
      position="relative"
      overflow="hidden"
    >
      {/* Left Panel */}
      <Box
        width={`${leftWidth}%`}
        height="100%"
        transition={isDragging.current ? "none" : "width 0.3s ease-in-out"}
        overflow="hidden"
        position="relative"
      >
        {leftPanel}
      </Box>

      {/* Resizer Handle */}
      {isRightPanelVisible && (
        <Box
          width="8px"
          height="100%"
          cursor="col-resize"
          bg="gray.200"
          _hover={{ bg: "gray.300" }}
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onMouseDown={startDrag}
          transition="background-color 0.2s"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.300"
        >
          <Icon
            as={FaGripVertical}
            color="gray.500"
            boxSize="12px"
            _hover={{ color: "gray.700" }}
          />
        </Box>
      )}

      {/* Right Panel */}
      {isRightPanelVisible && (
        <Box
          width={`${rightWidth}%`}
          height="100%"
          transition={isDragging.current ? "none" : "width 0.3s ease-in-out"}
          overflow="hidden"
          position="relative"
          bg="white"
          borderLeft="1px solid"
          borderColor="gray.200"
        >
          {rightPanel}
        </Box>
      )}
    </Flex>
  );
};

export default SplitView;
